/* ════════════════════════════════════════════════════════════════════
   LOG RALAT — SISI WEBAPP
   ════════════════════════════════════════════════════════════════════

   Sehingga hari ini HANYA extension yang menulis ke `error_logs`. Webapp
   langsung tidak — jadi setiap kali skrin "Component Crash" muncul di
   depan pelanggan, tiada seorang pun daripada kita yang tahu. Aduan
   2026-09-01 ("ramai kena") tidak dapat dijawab dengan angka kerana
   angka itu memang tidak pernah wujud.

   ⚠️ Sentry pun TIDAK menangkapnya. Sentry dipasang sebagai sempadan di
      akar aplikasi, tetapi halaman Workspace dibalut sempadan ralatnya
      sendiri yang menangkap DAHULU — jadi ralat itu tidak pernah naik
      setinggi Sentry. Fail ini menutup kedua-dua lubang sekali gus: ia
      menulis ke `error_logs` DAN menghantar ke Sentry secara jelas.

   Bentuk dokumen sengaja SAMA dengan yang ditulis extension
   (errorLogger.ts) — `source` sahaja yang membezakan ('webapp' lwn
   'extension'). Dengan itu satu paparan admin boleh membaca kedua-duanya
   tanpa perlu tahu asalnya.

   ⛔ Rules SUDAH mengizinkan ini: `error_logs` menerima `create` daripada
      mana-mana pengguna log masuk, dan `read`/`delete` admin sahaja. Jadi
      TIADA deploy rules diperlukan.

   ⚠️ KOS: dihadkan kepada 10 tulisan seminit setiap browser, ditambah
      penyahduaan 60 saat bagi ralat yang sama. Laporan perisai DOM pula
      sekali sahaja setiap sesi browser. Beban sebenar hampir sifar.
*/

import * as Sentry from '@sentry/react';
import { auth, db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { APP_VERSION } from '../version';
import { bacaDomGuard, capJariDom, adaGangguanDom } from '../utils/domGuard';

const HAD_SEMINIT = 10;
const TETINGKAP = 60000;
const TETINGKAP_NYAHDUA = 60000;

let kiraTulisan = 0;
let mulaTetingkap = Date.now();
const ralatTerkini = new Map();

function lulusHad() {
    const kini = Date.now();
    if (kini - mulaTetingkap > TETINGKAP) {
        kiraTulisan = 0;
        mulaTetingkap = kini;
    }
    kiraTulisan += 1;
    return kiraTulisan <= HAD_SEMINIT;
}

function lulusNyahdua(kunci) {
    const kini = Date.now();
    const kaliAkhir = ralatTerkini.get(kunci);
    if (kaliAkhir && kini - kaliAkhir < TETINGKAP_NYAHDUA) return false;
    ralatTerkini.set(kunci, kini);
    if (ralatTerkini.size > 50) {
        for (const [k, t] of ralatTerkini) {
            if (kini - t > TETINGKAP_NYAHDUA) ralatTerkini.delete(k);
        }
    }
    return true;
}

/* Firestore menolak `undefined`, dan nilai panjang membazir ruang tanpa
   menambah maklumat. Semua nilai `extra` melalui penapis ini. */
function kemas(nilai) {
    if (nilai === undefined || nilai === null) return null;
    if (Array.isArray(nilai)) return nilai.join(', ').slice(0, 300) || null;
    if (typeof nilai === 'string') return nilai.slice(0, 300);
    if (typeof nilai === 'number' || typeof nilai === 'boolean') return nilai;
    return String(nilai).slice(0, 300);
}

/* Penulis tunggal. SENYAP sepenuhnya: laluan laporan tidak boleh menjadi
   punca ralat baharu, dan tidak boleh menambah bunyi pada konsol
   pelanggan yang sudah pun menghadapi masalah. */
async function tulis({ message, stack, context, extra }) {
    try {
        if (!lulusHad()) return;
        const kunci = context + '::' + String(message).slice(0, 100);
        if (!lulusNyahdua(kunci)) return;

        const pengguna = auth.currentUser;
        // Rules menuntut log masuk — tulisan tanpa auth pasti ditolak.
        if (!pengguna) return;

        const cap = capJariDom();
        const guard = bacaDomGuard();

        const dok = {
            message: String(message || 'Unknown error').slice(0, 500),
            stack: stack ? String(stack).slice(0, 800) : null,
            context: context || 'unknown',
            userId: pengguna.uid,
            userEmail: pengguna.email || 'anonymous',
            version: APP_VERSION,
            source: 'webapp',
            timestamp: serverTimestamp(),
            url: String(window.location.href || '').slice(0, 200),
            extra: {
                ...(extra || {}),
                /* Cap jari persekitaran — inilah yang menjawab "extension
                   mana". Ia dilampirkan pada SETIAP laporan, bukan hanya
                   laporan perisai, kerana ralat lain pun boleh berpunca
                   daripada halaman yang diusik. */
                guardRemoveChild: guard.removeChild,
                guardInsertBefore: guard.insertBefore,
                guardContoh: kemas(guard.contohPertama),
                extDikesan: kemas(cap.dikesan) || 'tiada',
                extSuntikan: kemas(cap.suntikan) || 'tiada',
                bahasaHalaman: kemas(cap.bahasaHalaman),
                bahasaBrowser: kemas(cap.bahasaBrowser),
                skrin: window.innerWidth + 'x' + window.innerHeight,
                ua: kemas(navigator.userAgent),
            },
        };

        Sentry.withScope((skop) => {
            skop.setTag('context', dok.context);
            skop.setTag('source', 'webapp');
            skop.setTag('extDikesan', dok.extra.extDikesan);
            skop.setUser({ id: pengguna.uid, email: pengguna.email || undefined });
            skop.setExtras(dok.extra);
            Sentry.captureMessage(dok.message, 'error');
        });

        await addDoc(collection(db, 'error_logs'), dok);
    } catch {
        /* Gagal senyap — SELAMANYA. Melempar dari sini bermakna sempadan
           ralat menangkap laporan tentang ralat, dan gelung itu tidak
           pernah berakhir. */
    }
}

/* Dipanggil daripada sempadan ralat halaman. */
export function logCrash(error, errorInfo, context) {
    const mesej = error instanceof Error ? error.message : String(error);
    tulis({
        message: mesej,
        stack: error instanceof Error ? error.stack : null,
        context: context || 'react_crash',
        extra: {
            komponen: kemas(errorInfo && errorInfo.componentStack),
            laluan: kemas(window.location.pathname),
        },
    });
}

/* Laporan PERISAI — sekali setiap sesi browser.

   ⭐ Inilah bahagian yang menjawab soalan "berapa ramai sebenarnya".
      Selepas perisai dipasang, kemalangan berhenti berlaku, jadi
      `logCrash` di atas akan senyap. Tanpa laporan ini kita akan
      menyangka masalah itu lenyap sedangkan ia cuma tidak lagi kelihatan.

   ⛔ Kunci SESI, bukan localStorage: kita mahu satu laporan setiap
      pembukaan browser. localStorage bermakna pelanggan yang sama hanya
      dikira SEKALI SELAMANYA, dan trend mingguan menjadi mustahil. */
const KUNCI_SESI = 'sesaat_domguard_dilapor';

export function laporGangguanDom() {
    try {
        if (!adaGangguanDom()) return;
        if (sessionStorage.getItem(KUNCI_SESI)) return;
        sessionStorage.setItem(KUNCI_SESI, '1');
    } catch {
        /* Storan sesi disekat (mod privasi). Teruskan melaporkan — satu
           laporan tambahan jauh lebih murah daripada tiada data langsung. */
    }
    const guard = bacaDomGuard();
    tulis({
        message: 'Perisai DOM bertindak ' + guard.jumlah + ' kali (removeChild '
            + guard.removeChild + ', insertBefore ' + guard.insertBefore + ')',
        stack: null,
        context: 'dom_guard',
        extra: { laluan: kemas(window.location.pathname) },
    });
}
